<template>
  <el-row>
    <el-col style="padding-left: 5px; padding-right: 5px; height: 200px; width: 300px">
      <highcharts v-if="!isEmptyData" class="chart" style="height: 95%; width: 100%; margin-top: 10px;" :options="pieChartOptions" ref="highchart" />
      <div v-else class="empty_box">
        <span class="empty_text">데이터가 없습니다.</span>
      </div>
    </el-col>
    <el-col style="padding-left: 5px; padding-right: 5px; height: 200px; width: calc(100% - 310px)">
      <highcharts style="height: 95%; width: 100%; margin-top: 10px;" :options="lineChartOptions" ref="highchart" />
    </el-col>
  </el-row>
</template>

<script>
import { mapGetters } from 'vuex';
import { isNil, isEmpty, cloneDeep } from 'lodash';
import moment from 'moment';
import { pieOptions, lineOptions } from '@/constants/chart-options';
import { getDateToString } from '@/utils/date';
import amsSummaryApi from '@/api/ams/summary';
import amsResultApi from '@/api/ams/result';

export default {
  name: 'WTriggerExecResultChartView',
  props: {
    tabActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pieChartOptions: cloneDeep(pieOptions),
      lineChartOptions: cloneDeep(lineOptions),
      isEmptyData: true,
      resultStatusList: [
        {
          name: '성공',
          resultStatus: '성공',
          resultFlag: 'S',
          color: 'rgb(86, 139, 210)',
          zIndex: 3,
        },
        {
          name: '실패',
          resultStatus: '실패',
          resultFlag: 'F',
          color: 'rgb(253, 116, 132)',
          zIndex: 2,
        },
        {
          name: '부분성공',
          resultStatus: '부분성공',
          resultFlag: 'PS',
          color: 'rgb(247, 186, 0)',
          zIndex: 1,
        },
      ],
    };
  },
  computed: {
    colorLabelSet() {
      return this.resultStatusList.reduce((acc, val) => {
        acc[val.resultFlag] = { color: val.color, name: val.name };
        return acc;
      }, {});
    },
    ...mapGetters(['analysisDateParams']),
  },
  watch: {
    analysisDateParams() {
      this.loadData();
    },
    tabActive: {
      handler(flag) {
        if (flag) {
          this.loadData();
        }
      },
      immediate: true,
    },
  },
  created() {
    this.setChartOption();
  },
  methods: {
    loadData() {
      if (!this.tabActive) return;
      // pie data
      const params = {
        startDatetime: this.analysisDateParams.startDatetime,
        endDatetime: this.analysisDateParams.endDatetime,
      };
      amsSummaryApi.getTrgExecResultFlagCount(params).then((response) => {
        const result = response.data;
        if (result.success) {
          this.setPieChartData(cloneDeep(result.data));
        }
      });

      // line data
      amsResultApi.getTriggerExectionResultStackedColumnChartData({
        resultStatusList: this.resultStatusList,
        ...params,
      }).then((response) => {
        const result = response.data;
        if (result.success) {
          this.setLineChartData(cloneDeep(result.data));
        }
      });
    },
    /**
     * 차트 옵션 설정
     * @author jeee12
     */
    setChartOption() {
      const clickFunc = () => {
        this.$emit('change-active-tab', 'result');
      };
      const me = this;
      this.pieChartOptions.chart.spacing = [0, 0, 0, 0];
      this.pieChartOptions.plotOptions = {
        series: {
          className: 'c-pointer',
          point: {
            events: {
              click() {
                const item = {
                  ...this.options,
                  tagType: 'info',
                  type: 'resultStatus',
                  selected: true,
                  label: this.options.name,
                };
                me.$emit('change-active-tab', 'result', 'TRIGGER', [item]);
              },
            },
          },
        },
        pie: {
          dataLabels: {
            enabled: false,
          },
          innerSize: '30%',
        },
      };
      this.lineChartOptions.yAxis.allowDecimals = false;
      Object.assign(this.lineChartOptions.plotOptions.series, { className: 'c-pointer', events: { click: clickFunc } });
      Object.assign(this.lineChartOptions.tooltip, {
        formatter() {
          let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm');
          this.points.forEach((point) => {
            tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${point.y}</b><br/>`;
          });
          return tooltip;
        },
      });
    },
    /**
     * 파이 차트 데이터 바인딩
     * @author jeee12
     * @param  {[type]} data [description]
     */
    setPieChartData(data) {
      this.isEmptyData = isEmpty(data);
      data.forEach((item) => {
        item.name = isNil(item.key) ? ' ' : this.colorLabelSet[item.key].name;
        item.color = this.colorLabelSet[item.key].color;
      });
      this.pieChartOptions.series = [
        {
          name: '건수',
          colorByPoint: true,
          data,
        },
      ];
    },
    /**
     * 라인 차트 데이터 바인딩
     * @author jeee12
     * @param  {[type]} data [description]
     */
    setLineChartData(data) {
      const startDatetime = moment(this.analysisDateParams.startDatetime);
      const endDatetime = moment(this.analysisDateParams.endDatetime);
      const diff = endDatetime.diff(startDatetime, 'hours');

      let labelFormat = '{value: %Y-%m-%d}';
      let interval = null;
      let tooltipFormat = '%Y-%m-%d';
      if (diff < 1) {
        labelFormat = '{value: %H:%M}';
        tooltipFormat += ' %H:%M';
      } else if (diff > 0 && diff <= 24) {
        labelFormat = '{value: %H시}';
        interval = 3600 * 1000;
        tooltipFormat += ' %H시';
      }
      this.lineChartOptions.xAxis.tickInterval = interval;
      Object.assign(this.lineChartOptions.xAxis.labels, { format: labelFormat });
      this.lineChartOptions.tooltip.xDateFormat = tooltipFormat;
      this.lineChartOptions.series = data;
    },
  },
};
</script>

<style lang="scss" scoped>
.empty_box {
  width: 100%;
  min-height: 180px;
  display: table;
  .empty_text {
    display: table-cell;
    vertical-align: middle;
    // text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/work/popup/components/ExecutionSummary/components/TriggerExecResultChartView.vue